import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Document, Page, pdfjs } from 'react-pdf';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Helper function to load image as base64
const loadImageAsBase64 = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading image:', error);
    return null;
  }
};

export const generateTicketPDF = async (ticket, project, floor, comments, t, dateFormat, formatDateTimeBySetting, formatDateBySetting, getFileUrl, roleDisplayNames = {}, usersMap = {}) => {
  // Capture floor plan screenshot using off-screen rendering (no dialog needed)
  let floorPlanImageData = null;
  
  if (floor?.pdf_path && ticket.coordinate_x != null && ticket.coordinate_y != null) {
    try {
      console.log('Starting off-screen floor plan capture...');
      console.log('PDF path:', floor.pdf_path);
      console.log('Ticket coordinates:', ticket.coordinate_x, ticket.coordinate_y);
      
      // Create hidden container for PDF rendering
      const hiddenContainer = document.createElement('div');
      hiddenContainer.id = 'pdf-offscreen-renderer';
      hiddenContainer.style.cssText = `
        position: absolute;
        left: -9999px;
        top: 0;
        width: 1200px;
        height: 1200px;
        background: white;
        overflow: hidden;
      `;
      document.body.appendChild(hiddenContainer);
      
      // Create promises for PDF loading with error tracking
      let resolvePdfLoad;
      let rejectPdfLoad;
      let pdfLoadSuccess = false;
      
      const pdfLoadPromise = new Promise((resolve, reject) => {
        resolvePdfLoad = resolve;
        rejectPdfLoad = reject;
      });
      
      // Render the PDF with marker using React
      const pdfUrl = floor.pdf_path.replace('/uploads/', '/api/files/');
      console.log('Loading PDF from:', pdfUrl);
      const scale = 1.5; // Optimal scale for quality and context
      
      const FloorPlanComponent = React.createElement(
        'div',
        { 
          style: { 
            position: 'relative', 
            display: 'inline-block',
            background: 'white'
          },
          id: 'floor-plan-capture-root'
        },
        [
          // PDF Document
          React.createElement(
            Document,
            {
              key: 'pdf-doc',
              file: pdfUrl,
              onLoadSuccess: (pdf) => {
                console.log('✓ PDF loaded successfully with', pdf.numPages, 'pages');
                pdfLoadSuccess = true;
                // Give extra time for page rendering
                setTimeout(() => resolvePdfLoad(pdf), 1000);
              },
              onLoadError: (error) => {
                console.error('✗ PDF load error:', error);
                pdfLoadSuccess = false;
                rejectPdfLoad(error);
              },
              loading: React.createElement('div', { 
                style: { padding: '20px', textAlign: 'center' } 
              }, 'Loading PDF...')
            },
            React.createElement(Page, {
              key: 'pdf-page',
              pageNumber: 1,
              scale: scale,
              renderTextLayer: false,
              renderAnnotationLayer: false,
              onLoadSuccess: () => {
                console.log('✓ Page rendered successfully');
              },
              onRenderError: (error) => {
                console.error('✗ Page render error:', error);
              }
            })
          ),
          // Marker overlay
          React.createElement(
            'div',
            {
              key: 'marker',
              style: {
                position: 'absolute',
                left: `${ticket.coordinate_x}%`,
                top: `${ticket.coordinate_y}%`,
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
                pointerEvents: 'none'
              }
            },
            React.createElement(
              'div',
              {
                style: {
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  border: '4px solid white'
                }
              },
              // MapPin SVG icon
              React.createElement(
                'svg',
                {
                  xmlns: 'http://www.w3.org/2000/svg',
                  width: '32',
                  height: '32',
                  viewBox: '0 0 24 24',
                  fill: 'none',
                  stroke: 'white',
                  strokeWidth: '2',
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round'
                },
                [
                  React.createElement('path', { key: 'path1', d: 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z' }),
                  React.createElement('circle', { key: 'circle', cx: '12', cy: '10', r: '3' })
                ]
              )
            )
          )
        ]
      );
      
      // Mount React component
      const root = ReactDOM.createRoot(hiddenContainer);
      root.render(FloorPlanComponent);
      
      // Wait for PDF to load with timeout
      try {
        await Promise.race([
          pdfLoadPromise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('PDF load timeout')), 10000))
        ]);
      } catch (error) {
        console.error('PDF loading failed or timed out:', error);
        // Cleanup and exit
        try {
          root.unmount();
          document.body.removeChild(hiddenContainer);
        } catch (e) {}
        throw error;
      }
      
      // Check if PDF actually loaded successfully
      if (!pdfLoadSuccess) {
        console.error('PDF did not load successfully, cannot capture');
        try {
          root.unmount();
          document.body.removeChild(hiddenContainer);
        } catch (e) {}
        throw new Error('PDF failed to load');
      }
      
      // Give extra time for all rendering to complete
      console.log('Waiting for rendering to complete...');
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Capture the rendered content
      const captureElement = document.getElementById('floor-plan-capture-root');
      
      if (captureElement && captureElement.children.length > 0) {
        console.log('Capturing rendered floor plan...');
        
        const fullCanvas = await html2canvas(captureElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: '#ffffff'
        });
        
        // Calculate 25% x 25% crop area centered on ticket location
        // User requirement: extend 12.5% in all 4 directions from ticket center
        const fullWidth = fullCanvas.width;
        const fullHeight = fullCanvas.height;
        
        // Crop dimensions: 25% of full width and height
        const cropWidth = fullWidth * 0.25;
        const cropHeight = fullHeight * 0.25;
        
        // Ticket position in pixels
        const ticketX = (ticket.coordinate_x / 100) * fullWidth;
        const ticketY = (ticket.coordinate_y / 100) * fullHeight;
        
        // Calculate crop boundaries (extend 12.5% in each direction from ticket center)
        let cropX = ticketX - (cropWidth / 2);  // 12.5% to the left
        let cropY = ticketY - (cropHeight / 2); // 12.5% to the top
        
        // Ensure crop stays within canvas boundaries
        cropX = Math.max(0, Math.min(cropX, fullWidth - cropWidth));
        cropY = Math.max(0, Math.min(cropY, fullHeight - cropHeight));
        
        console.log('Crop details:', {
          fullSize: `${fullWidth}x${fullHeight}`,
          cropSize: `${cropWidth.toFixed(0)}x${cropHeight.toFixed(0)}`,
          ticketPos: `${ticketX.toFixed(0)},${ticketY.toFixed(0)}`,
          cropPos: `${cropX.toFixed(0)},${cropY.toFixed(0)}`,
          percentage: '25% x 25%'
        });
        
        // Create cropped canvas
        const croppedCanvas = document.createElement('canvas');
        croppedCanvas.width = cropWidth;
        croppedCanvas.height = cropHeight;
        const ctx = croppedCanvas.getContext('2d');
        
        ctx.drawImage(
          fullCanvas,
          cropX, cropY, cropWidth, cropHeight,
          0, 0, cropWidth, cropHeight
        );
        
        floorPlanImageData = croppedCanvas.toDataURL('image/jpeg', 0.9);
        console.log('✓ Floor plan captured successfully with 25% x 25% crop!');
      } else {
        console.warn('Could not find capture element or it has no children');
        throw new Error('Capture element not found');
      }
      
      // Cleanup
      root.unmount();
      document.body.removeChild(hiddenContainer);
      
    } catch (error) {
      console.error('Error capturing floor plan:', error);
      // Clean up on error
      const hiddenContainer = document.getElementById('pdf-offscreen-renderer');
      if (hiddenContainer) {
        try {
          document.body.removeChild(hiddenContainer);
        } catch (e) {
          console.error('Error cleaning up hidden container:', e);
        }
      }
    }
  }

  // Create a hidden container for rendering
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '794px'; // A4 width in pixels at 96 DPI
  container.style.background = 'white';
  container.style.padding = '40px';
  container.style.fontFamily = '"Noto Sans TC", "Microsoft JhengHei", "PingFang TC", "Heiti TC", sans-serif';
  
  document.body.appendChild(container);

  // Build HTML content
  let html = `
    <div style="font-family: 'Noto Sans TC', 'Microsoft JhengHei', sans-serif; font-size: 14px; line-height: 1.2; color: #000000;">
      <!-- Header -->
      <div style="text-align: center; margin-bottom: 12px; border-bottom: 2px solid #333; padding-bottom: 10px;">
        <h1 style="font-size: 24px; font-weight: bold; margin: 0 0 8px 0; color: #000000; line-height: 1.2;">${project?.name || ''}</h1>
        <h2 style="font-size: 20px; font-weight: bold; margin: 0; color: #000000; line-height: 1.2;">${floor?.name || ''}</h2>
      </div>

      <!-- Ticket Code and Status (on same line) -->
      <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 12px;">
        <div style="font-size: 13px; color: #000000;">
          <strong style="color: #000000;">${t('tickets.ticketCode')}:</strong> ${ticket.ticket_code || ''}
        </div>
        <div style="background: ${getStatusColor(ticket.status)}; color: white; padding: 8px 15px 7px 15px; border-radius: 4px; font-size: 13px; font-weight: bold; display: inline-block; line-height: 1; vertical-align: middle;">
          ${t(`tickets.statuses.${ticket.status === 'in_progress' ? 'inProgress' : ticket.status}`)}
        </div>
      </div>

      <!-- Two Column Layout -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
        <!-- Left Column -->
        <div>
          <h3 style="font-size: 16px; font-weight: bold; margin-bottom: 8px; color: #000000; line-height: 1.2;">${ticket.title}</h3>
          
          <div style="margin-bottom: 12px; color: #000000; line-height: 1.5;">
            <div style="margin-bottom: 5px; color: #000000;"><strong style="color: #000000;">${t('tickets.priority')}:</strong> ${t(`tickets.priorities.${ticket.priority}`)}</div>
            <div style="margin-bottom: 5px; color: #000000;"><strong style="color: #000000;">${t('tickets.category')}:</strong> ${ticket.category}</div>
            <div style="margin-bottom: 5px; color: #000000;"><strong style="color: #000000;">${t('tickets.createdBy')}:</strong> ${ticket.created_by_name || ticket.created_by}</div>
            <div style="margin-bottom: 5px; color: #000000;"><strong style="color: #000000;">${t('tickets.assignedTo')}:</strong> ${ticket.assigned_to_names?.join(', ') || t('tickets.unassigned')}</div>
            <div style="margin-bottom: 5px; color: #000000;"><strong style="color: #000000;">${t('tickets.createdAt')}:</strong> ${formatDateTimeBySetting(ticket.created_at, dateFormat)}</div>
            ${ticket.deadline ? `<div style="margin-bottom: 5px; color: #000000;"><strong style="color: #000000;">${t('tickets.deadline')}:</strong> ${formatDateBySetting(ticket.deadline, dateFormat)}</div>` : ''}
          </div>
        </div>

        <!-- Right Column - Location -->
        <div>
          <div style="font-weight: bold; margin-bottom: 8px; color: #000000;">
            <span>${t('tickets.location')}:</span>
            <span style="margin-left: 8px; font-weight: normal;">${floor?.name || ''}</span>
          </div>
          <div id="location-screenshot-container" style="border: 2px solid #333; display: flex; align-items: center; justify-content: center; background: #f5f5f5; width: 300px; height: 200px;">
            <span style="color: #000000; font-weight: bold;">${t('tickets.locationNotAvailable')}</span>
          </div>
        </div>
      </div>

      <!-- Description Section -->
      <div style="margin-top: 15px; border-top: 2px solid #333; padding-top: 10px; padding-bottom: 10px;">
        <div style="font-weight: bold; font-size: 15px; margin-bottom: 8px; color: #000000;">${t('tickets.description')}:</div>
        <div style="white-space: pre-wrap; color: #000000;">${ticket.description || ''}</div>
      </div>
`;

  // Calculate space for Photos section - split rows intelligently
  if (ticket.photos && ticket.photos.length > 0) {
    const contentPageHeight = 2086;
    
    // Estimate height before Photos section
    const estimatedHeightBeforePhotos = 200 + 80 + 440 + 120; // Header + Code + Details + Description
    const spaceLeftOnFirstPage = contentPageHeight - estimatedHeightBeforePhotos;
    
    // Calculate photo rows
    const totalRows = Math.ceil(ticket.photos.length / 2);
    const photoTitleHeight = 60; // Title height at scale 2
    const rowHeight = 560; // Each row height at scale 2
    
    // Calculate how many rows can fit on current page
    const spaceForPhotos = spaceLeftOnFirstPage - photoTitleHeight - 30; // minus title and margins
    const rowsThatFit = Math.max(0, Math.floor(spaceForPhotos / rowHeight));
    const photosThatFit = Math.min(rowsThatFit * 2, ticket.photos.length);
    const remainingPhotos = ticket.photos.length - photosThatFit;
    
    console.log('Photos section smart split:', {
      totalPhotos: ticket.photos.length,
      totalRows,
      spaceLeftOnFirstPage,
      rowsThatFit,
      photosThatFit,
      remainingPhotos
    });
    
    // Start Photos section
    html += `
      <div style="margin-top: 15px; border-top: 2px solid #333; padding-top: 10px;">
        <div style="font-weight: bold; font-size: 15px; margin-bottom: 13px; color: #000000;">${t('tickets.photos')}:</div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; column-gap: 15px; row-gap: 15px;">
    `;
    
    // Add photos that fit on current page
    for (let i = 0; i < photosThatFit; i++) {
      const photoUrl = getFileUrl(ticket.photos[i]);
      html += `
        <div style="height: 250px; display: flex; align-items: center; justify-content: center;">
          <img src="${photoUrl}" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
        </div>
      `;
    }
    
    html += `
        </div>
    `;
    
    // If there are remaining photos, add page break and continue
    if (remainingPhotos > 0) {
      // Add spacer to push remaining photos to next page
      const remainingSpace = spaceLeftOnFirstPage - photoTitleHeight - (rowsThatFit * rowHeight);
      html += `
      </div>
      <div style="height: ${Math.max(remainingSpace + 100, 100)}px;"></div>
      <div style="border-top: 2px solid #333; padding-top: 10px;">
        <div style="display: grid; grid-template-columns: 1fr 1fr; column-gap: 15px; row-gap: 15px;">
      `;
      
      // Add remaining photos on next page
      for (let i = photosThatFit; i < ticket.photos.length; i++) {
        const photoUrl = getFileUrl(ticket.photos[i]);
        html += `
          <div style="height: 250px; display: flex; align-items: center; justify-content: center;">
            <img src="${photoUrl}" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
          </div>
        `;
      }
      
      html += `
        </div>
      </div>
      `;
    } else {
      html += `</div>`;
    }
  }

  // Calculate space for Activity section and add spacer if needed
  if (comments && comments.length > 0) {
    // A4 dimensions: 210mm x 297mm
    // At 96 DPI: 794px x 1123px (before scale)
    // At scale 2: 1588px x 2246px (canvas dimensions)
    // Content area with 40px padding: 714px x 1043px (before scale)
    // At scale 2: 1428px x 2086px
    
    const contentPageHeight = 2086; // A4 content height at scale 2 (297mm - 80px padding = 1043px * 2)
    
    // Estimate height of content before Activity section
    // Header (title + floor): ~100px * 2 = 200px
    // Ticket code + status: ~40px * 2 = 80px  
    // Two column layout (details + location): ~220px * 2 = 440px
    // Description section: ~60px * 2 = 120px
    // Photos section header: ~30px * 2 = 60px
    // Photos: each row is ~280px * 2 = 560px at scale 2
    
    const photoRows = ticket.photos ? Math.ceil(ticket.photos.length / 2) : 0;
    const estimatedHeightBeforeActivity = 200 + 80 + 440 + 120 + 60 + (photoRows * 560);
    
    const spaceLeftOnFirstPage = contentPageHeight - estimatedHeightBeforeActivity;
    
    // Estimate Activity section minimum height
    // Section title + border: ~50px * 2 = 100px
    // First comment minimum: ~120px * 2 = 240px
    const activityMinHeight = 340;
    
    // If not enough space for Activity section, add spacer to push to next page
    const needsNewPage = spaceLeftOnFirstPage < activityMinHeight;
    const spacerHeight = needsNewPage ? Math.max(spaceLeftOnFirstPage + 100, 0) : 20;
    
    console.log('Activity section placement (A4 based):', {
      contentPageHeight,
      estimatedHeightBeforeActivity,
      spaceLeftOnFirstPage,
      activityMinHeight,
      needsNewPage,
      spacerHeight,
      photoRows
    });
    
    html += `
      <div style="margin-top: ${spacerHeight}px; border-top: 2px solid #333; padding-top: 10px;">
        <div style="font-weight: bold; font-size: 15px; margin-bottom: 13px; color: #000000;">${t('comments.activityAndComments')}:</div>
    `;
    
    for (const comment of comments) {
      // The correct field name from backend is 'content'
      const commentText = comment.content || comment.text || comment.comment || '';
      
      // Get translated role name for display
      const roleName = roleDisplayNames[comment.user_role] || comment.user_role_display || comment.user_role || '';
      
      
      html += `
        <div style="border: 2px solid #333; border-radius: 8px; padding: 10px; margin-bottom: 20px; background: #ffffff; position: relative;">
          <!-- Status Badge in Top Right Corner -->
          ${comment.approval_status === 'approved' ? `<div style="position: absolute; top: 10px; right: 10px; color: white; font-weight: bold; font-size: 11px; background: #22c55e; padding: 6px 8px 5px 8px; border-radius: 4px; display: inline-block; line-height: 1; vertical-align: middle;">✓ ${t('comments.approved')}</div>` : ''}
          ${comment.approval_status === 'rejected' ? `<div style="position: absolute; top: 10px; right: 10px; color: white; background: #ef4444; font-weight: bold; font-size: 11px; padding: 6px 8px 5px 8px; border-radius: 4px; display: inline-block; line-height: 1; vertical-align: middle;">✗ ${t('comments.rejected')}</div>` : ''}
          ${comment.approval_status === 'pending' ? `<div style="position: absolute; top: 10px; right: 10px; color: #000000; background: #eab308; font-weight: bold; font-size: 11px; padding: 6px 8px 5px 8px; border-radius: 4px; display: inline-block; line-height: 1; vertical-align: middle;">⏳ ${t('comments.pendingApproval')}</div>` : ''}
          
          <!-- User Name and Role -->
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px; ${comment.approval_status ? 'padding-right: 120px;' : ''}">
            <span style="font-weight: bold; color: #000000;">${comment.user_name || comment.user_id || ''}</span>
            ${roleName ? `<span style="background: #666666; color: white; padding: 4px 8px 3px 8px; border-radius: 4px; font-size: 10px; font-weight: normal; display: inline-block; line-height: 1; vertical-align: middle;">${roleName}</span>` : ''}
          </div>
          
          <!-- Timestamp -->
          <div style="font-size: 12px; color: #666666; margin-bottom: 6px; font-weight: normal;">${formatDateTimeBySetting(comment.created_at, dateFormat)}</div>
          
          <!-- Comment Content -->
          <div style="margin-bottom: 6px; color: #000000; font-size: 13px; white-space: pre-wrap;">${commentText}</div>
          
          <!-- Comment Photos -->
          ${comment.photos && comment.photos.length > 0 ? `
            <div style="display: grid; grid-template-columns: 1fr 1fr; column-gap: 15px; row-gap: 15px; margin-top: 8px;">
              ${comment.photos.map(photo => `
                <div style="height: 250px; display: flex; align-items: center; justify-content: center;">
                  <img src="${getFileUrl(photo)}" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
                </div>
              `).join('')}
            </div>
          ` : ''}
          
          <!-- Approval Note (after photos) -->
          ${(comment.approval_status === 'approved' || comment.approval_status === 'rejected') && comment.approved_by ? `
            <div style="margin-top: 8px; padding: 8px; background: #f5f5f5; border-left: 3px solid ${comment.approval_status === 'approved' ? '#22c55e' : '#ef4444'}; border-radius: 4px;">
              <div style="margin-bottom: 4px;">
                <span style="font-weight: bold; color: #000000; font-size: 12px;">${usersMap[comment.approved_by]?.name || t('users.unknown')}</span>
                ${usersMap[comment.approved_by]?.role ? `<span style="background: #666666; color: white; padding: 3px 6px 2px 6px; border-radius: 3px; font-size: 9px; margin-left: 6px; display: inline-block; line-height: 1; vertical-align: middle;">${roleDisplayNames[usersMap[comment.approved_by].role] || usersMap[comment.approved_by].role}</span>` : ''}
                <span style="color: #666666; font-size: 12px;"> ${comment.approval_status === 'approved' ? t('comments.approvedThis') : t('comments.rejectedThis')}</span>
              </div>
              ${comment.approval_note ? `
                <div style="margin-top: 4px;">
                  <span style="font-weight: bold; color: #000000; font-size: 12px;">${t('comments.note')}:</span>
                  <span style="color: #333333; font-size: 12px; font-style: italic;"> ${comment.approval_note}</span>
                </div>
              ` : ''}
            </div>
          ` : ''}
        </div>
      `;
    }
    
    html += `</div>`;
  }

  html += `</div>`;
  
  container.innerHTML = html;

  // Insert floor plan screenshot if captured
  if (floorPlanImageData) {
    const locationContainer = container.querySelector('#location-screenshot-container');
    if (locationContainer) {
      locationContainer.style.padding = '5px';
      locationContainer.style.width = '300px';
      locationContainer.style.height = '200px';
      locationContainer.innerHTML = `<img src="${floorPlanImageData}" style="width: 100%; height: 100%; object-fit: contain; display: block; border: none;" />`;
    }
  }

  // Helper function to get status color
  function getStatusColor(status) {
    const colors = {
      new: '#ef4444',
      in_progress: '#eab308',
      fixed: '#22c55e',
      rejected: '#f97316',
      closed: '#9ca3af'
    };
    return colors[status] || '#9ca3af';
  }

  // Wait for images to load
  await new Promise(resolve => setTimeout(resolve, 500));

  // Capture as image
  const canvas = await html2canvas(container, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: '#ffffff'
  });

  // Remove container
  document.body.removeChild(container);

  // Create PDF
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.9);
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  
  // Calculate image dimensions
  const imgWidth = pdfWidth;
  const imgHeight = (canvas.height * pdfWidth) / canvas.width;
  
  let heightLeft = imgHeight;
  let position = 0;

  // Add first page
  pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
  heightLeft -= pdfHeight;

  // Add additional pages if needed
  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;
  }

  // Save PDF
  const fileName = `${ticket.ticket_code || 'ticket'}_${new Date().getTime()}.pdf`;
  pdf.save(fileName);
};
